/**
 * Client HTTP pour le Worker Cloudflare.
 *
 * Dev  : https://mailfox-tracker-dev.jsonautos.workers.dev
 * Prod : https://i.autonao.com
 *
 * URL choisie via VITE_WORKER_URL (.env.development / .env.production).
 *
 * Phase 3 scaffold : juste ping + getVapidPublicKey. Le reste des endpoints
 * (devices subscribe/unsubscribe, OAuth proxy, events poll) sera ajouté
 * en Phase 3a/b.
 */

const WORKER_URL = import.meta.env.VITE_WORKER_URL;

if (!WORKER_URL) {
  throw new Error(
    "VITE_WORKER_URL not set — check .env.development / .env.production",
  );
}

/** Smoke test de connectivité. Utilisé par App.svelte au boot. */
export async function ping(): Promise<boolean> {
  try {
    const res = await fetch(`${WORKER_URL}/health`);
    return res.ok;
  } catch {
    return false;
  }
}

/**
 * Récupère la clé publique VAPID du Worker. Utilisée par
 * `pushManager.subscribe({ applicationServerKey })` quand on s'abonne aux push.
 */
export async function getVapidPublicKey(): Promise<string> {
  const res = await fetch(`${WORKER_URL}/vapid-public-key`);
  if (!res.ok) {
    throw new Error(`vapid-public-key: HTTP ${res.status}`);
  }
  const json = (await res.json()) as { publicKey: string };
  return json.publicKey;
}

// ─── OAuth proxy (Phase 3a) ─────────────────────────────────────────────────
//
// Le Worker fait l'échange auprès de Google avec le client_secret côté serveur.
// La PWA ne voit jamais le client_secret.

export interface ExchangeRequest {
  code: string;
  code_verifier: string;
  redirect_uri: string;
}

export interface ExchangeResponse {
  access_token: string;
  refresh_token: string;
  expires_in: number;
  id_token: string;
  token_type: string;
  scope?: string;
}

export interface RefreshRequest {
  refresh_token: string;
}

export interface RefreshResponse {
  access_token: string;
  expires_in: number;
  /** Présent uniquement si Google rotate le refresh_token (rare). */
  refresh_token?: string;
  /** Parfois présent au refresh, parfois non — on le garde si présent. */
  id_token?: string;
  token_type: string;
}

export async function oauthExchange(req: ExchangeRequest): Promise<ExchangeResponse> {
  const res = await fetch(`${WORKER_URL}/oauth/exchange`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(req),
  });
  if (!res.ok) {
    const errBody = await res.text();
    throw new Error(`oauth/exchange ${res.status}: ${errBody.slice(0, 300)}`);
  }
  return res.json() as Promise<ExchangeResponse>;
}

export async function oauthRefresh(req: RefreshRequest): Promise<RefreshResponse> {
  const res = await fetch(`${WORKER_URL}/oauth/refresh`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(req),
  });
  if (!res.ok) {
    const errBody = await res.text();
    throw new Error(`oauth/refresh ${res.status}: ${errBody.slice(0, 300)}`);
  }
  return res.json() as Promise<RefreshResponse>;
}

// ─── /me/* endpoints (Phase 3b) ──────────────────────────────────────────────
//
// Auth via Bearer Google access_token. Le Worker dérive l'email du token
// (pas de claim client) → impossible d'usurper. Différent du SHARED_SECRET
// utilisé par le desktop.

export interface MeOpensResponse {
  account_email: string;
  opens: Array<{
    id: number;
    pixel_id: string;
    opened_at: number; // unix sec
    country: string | null;
    region: string | null;
    city: string | null;
    user_agent: string | null;
    recipient_email: string;
    recipient_name: string | null;
    subject: string | null;
    sent_at: number;
  }>;
}

export interface MeDevicesCountResponse {
  account_email: string;
  count: number;
}

export interface MeDeviceSubscribeBody {
  subscription: PushSubscriptionJSON;
  user_agent?: string | null;
}

/** Helper interne — construit les headers d'auth Google. */
function authHeaders(googleAccessToken: string): HeadersInit {
  return {
    "Authorization": `Bearer ${googleAccessToken}`,
    "Content-Type": "application/json",
  };
}

export async function meDevicesGet(
  googleAccessToken: string,
): Promise<MeDevicesCountResponse> {
  const res = await fetch(`${WORKER_URL}/me/devices`, {
    headers: authHeaders(googleAccessToken),
  });
  if (!res.ok) {
    throw new Error(`me/devices ${res.status}: ${(await res.text()).slice(0, 200)}`);
  }
  return res.json() as Promise<MeDevicesCountResponse>;
}

export async function meDevicesPost(
  googleAccessToken: string,
  body: MeDeviceSubscribeBody,
): Promise<{ ok: true; account_email: string }> {
  const res = await fetch(`${WORKER_URL}/me/devices`, {
    method: "POST",
    headers: authHeaders(googleAccessToken),
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    throw new Error(`me/devices POST ${res.status}: ${(await res.text()).slice(0, 200)}`);
  }
  return res.json() as Promise<{ ok: true; account_email: string }>;
}

export async function meDevicesDelete(
  googleAccessToken: string,
  endpoint: string,
): Promise<{ ok: true; deleted: number }> {
  const res = await fetch(`${WORKER_URL}/me/devices`, {
    method: "DELETE",
    headers: authHeaders(googleAccessToken),
    body: JSON.stringify({ endpoint }),
  });
  if (!res.ok) {
    throw new Error(`me/devices DELETE ${res.status}: ${(await res.text()).slice(0, 200)}`);
  }
  return res.json() as Promise<{ ok: true; deleted: number }>;
}

export async function meOpens(
  googleAccessToken: string,
  since = 0,
  limit = 100,
): Promise<MeOpensResponse> {
  const url = new URL(`${WORKER_URL}/me/opens`);
  url.searchParams.set("since", String(since));
  url.searchParams.set("limit", String(limit));
  const res = await fetch(url.toString(), {
    headers: authHeaders(googleAccessToken),
  });
  if (!res.ok) {
    throw new Error(`me/opens ${res.status}: ${(await res.text()).slice(0, 200)}`);
  }
  return res.json() as Promise<MeOpensResponse>;
}

// ─── /me/pixels (Phase 5b) ──────────────────────────────────────────────────
//
// Enregistre un mapping pixel ↔ recipient au moment du send. Le Worker
// derive account_email du token Google → impossible d'usurper.

export interface MePixelRegistration {
  pixel_id: string;
  recipient_email: string;
  recipient_name?: string | null;
  subject?: string | null;
  sent_at: number; // unix sec
}

export async function mePixelsPost(
  googleAccessToken: string,
  body: MePixelRegistration,
): Promise<{ ok: true; account_email: string }> {
  const res = await fetch(`${WORKER_URL}/me/pixels`, {
    method: "POST",
    headers: authHeaders(googleAccessToken),
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    throw new Error(`me/pixels ${res.status}: ${(await res.text()).slice(0, 200)}`);
  }
  return res.json() as Promise<{ ok: true; account_email: string }>;
}

/** URL du Worker exposée pour construction du pixel `<img src>` dans le mail. */
export function getWorkerUrl(): string {
  return WORKER_URL;
}
